import React, { useState } from 'react'
import $t from '../../../../i18n'
import { useDispatch, useSelector } from 'react-redux'
import Page from '../../../common/Page/Page'
import { HMOIntentOptions, clinicTypes } from '../../../../consts'
import { Button } from 'antd'
import Actions from '../../../../actions'
import {
  getOriginByIntent,
  hasMHAppointment,
  isFollowUp,
} from '../../../../utils/clinicsUtils'
import { isMemberMinorChild } from '../../../../utils/familyUtils'
import {
  isBotIntentAdministrativeOption,
  mapIntentToDefault,
} from '../../../../utils/hmoUtils'

import './IntentPage.css'
import { event, eventTypes } from '../../../../consts/eventConsts'

export const IntentPage = ({ onNext }) => {
  const dispatch = useDispatch()

  const antidoteClinics = useSelector(
    state => state.account?.clinicsData?.antidoteClinics
  )
  const userDetails = useSelector(state => state.profile?.userDetails)
  const selectedMember = useSelector(state => state.account?.selectedMember)

  const [selectedIntentOptions, setSelectedIntentOptions] = useState([])

  const isMinor = selectedMember ? isMemberMinorChild(selectedMember) : false
  // patient hmo services
  const hmoServices = selectedMember
    ? selectedMember?.hmo_services
    : userDetails?.hmo_services

  // TODO!! ANOTHER CLICK FOR MENTAL MINOR?!?!
  const isMHFollowUp = isFollowUp(clinicTypes.MENTAL, antidoteClinics)
  const isMHAppointmentAlreadyScheduled = useSelector(state =>
    hasMHAppointment(state)
  )

  const onIntentOptionSelected = option => {
    const cloneArray = [...selectedIntentOptions]

    if (cloneArray.includes(option)) {
      const index = cloneArray.indexOf(option)
      cloneArray.splice(index, 1)
    } else {
      cloneArray.push(option)
    }

    setSelectedIntentOptions(cloneArray)
  }

  const onOtherPressed = () => {
    const primaryOption = isMinor
      ? HMOIntentOptions.PEDIATRICK
      : HMOIntentOptions.PRIMARY
    setSelectedIntentOptions([primaryOption])
    onNextPressed([primaryOption])

    dispatch(
      Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
        type: eventTypes.formEvent,
        eventName: event.formEvent.submit,
        data: { intent: primaryOption },
        element: 'Intent screen other',
      })
    )
  }

  const shouldStartMentalBotFlow = selectedOptions => {
    const isMentalSelected = selectedOptions?.includes(HMOIntentOptions.MENTAL)
    const intentWithoutAdministrative = selectedOptions?.filter(
      option =>
        option !== HMOIntentOptions.PRESCRIPTIONS &&
        option !== HMOIntentOptions.MEDICAL_NOTE &&
        option !== HMOIntentOptions.WELLNESS_VISIT &&
        option !== HMOIntentOptions.REFERRALS
    )

    // in case mental is selected only, or mental and administrative, start mental flow
    if (isMentalSelected && intentWithoutAdministrative?.length === 1) {
      return true
    }

    return false
  }

  const onNextPressed = selectedOptions => {
    // the intent array should contain name and isAdministrative flag to understand if its bot or not
    const intentWithAdministrativeIndication = selectedOptions?.map(option => {
      const optionName = getOriginByIntent(option, hmoServices)
      return isBotIntentAdministrativeOption(optionName)
        ? { name: optionName, is_administrative: true }
        : { name: optionName, is_administrative: false }
    })

    dispatch(
      Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
        type: eventTypes.formEvent,
        eventName: event.formEvent.submit,
        data: intentWithAdministrativeIndication,
        element: 'Intent screen',
      })
    )

    // in case only administrative options are selected, we need to add the primary option to the intent
    const isNeedToPushDefault = mapIntentToDefault(
      intentWithAdministrativeIndication
    )

    const primaryOption = {
      name: isMinor ? HMOIntentOptions.PEDIATRICK : HMOIntentOptions.PRIMARY,
      is_administrative: false,
    }

    if (isNeedToPushDefault) {
      intentWithAdministrativeIndication?.push(primaryOption)
    }

    const shouldStartMental = shouldStartMentalBotFlow(selectedOptions)

    const payload = {
      intent: intentWithAdministrativeIndication,
      isMentalHealth: shouldStartMental,
    }
    dispatch(Actions.createAction(Actions.HMO_BOT_STARTED, payload))

    // in case we need to start mental, we are switching the selected service
    // this switch will cause the whoule get care wizard to render again, no need for next callback here
    // in case we will do the next, the mental bot will be skipped.
    if (onNext && !shouldStartMental) onNext()
  }

  const IntentOption = ({ intentOption, disclaimer, isDisabled }) => {
    const isSelected = selectedIntentOptions.includes(intentOption)
    return (
      <div
        className="option-description list-item"
        data-disabled={isDisabled}
        data-selected={isSelected}
        onClick={() => (isDisabled ? {} : onIntentOptionSelected(intentOption))}
      >
        <div className="row">
          {$t('intentPage.options.' + intentOption)}
          <i className="icon icon-oclose" />
        </div>
        {disclaimer && <div className="disclaimer">{disclaimer}</div>}
      </div>
    )
  }

  return (
    <Page
      className="intent-page"
      title={$t('intentPage.title')}
      subtitle={$t('intentPage.subtitle')}
    >
      <div className="intent-page-options">
        <div className="option-title semi-bold">
          {$t('intentPage.options.acuteCare')}
        </div>
        <IntentOption
          intentOption={
            isMinor ? HMOIntentOptions.PEDIATRICK : HMOIntentOptions.PRIMARY
          }
        />
        {
          // remove condition when we will have mental for minor
          !isMinor && (
            <div className="option-title semi-bold">
              {$t('intentPage.options.chronicCare')}
            </div>
          )
        }
        {
          // remove condition when we will have mental for minor
          !isMinor && (
            <IntentOption
              intentOption={
                isMinor ? HMOIntentOptions.MENTAL_KIDS : HMOIntentOptions.MENTAL
              }
              disclaimer={
                isMHFollowUp
                  ? $t('intentPage.options.mentalNoteFollowUp')
                  : isMHAppointmentAlreadyScheduled
                  ? $t('intentPage.options.mentalNote')
                  : null
              }
              isDisabled={isMHFollowUp || isMHAppointmentAlreadyScheduled}
            />
          )
        }
        {!isMinor && <IntentOption intentOption={HMOIntentOptions.HTN} />}
        {!isMinor && <IntentOption intentOption={HMOIntentOptions.DIABETES} />}
        <div className="option-title semi-bold">
          {$t('intentPage.options.administrative')}
        </div>
        <IntentOption intentOption={HMOIntentOptions.PRESCRIPTIONS} />
        <IntentOption intentOption={HMOIntentOptions.WELLNESS_VISIT} />
        <IntentOption intentOption={HMOIntentOptions.REFERRALS} />
        <IntentOption intentOption={HMOIntentOptions.MEDICAL_NOTE} />
        <Button className="option-other" type="link" onClick={onOtherPressed}>
          {$t('intentPage.options.other')}
        </Button>
      </div>
      <div className="intent-page-footer">
        <Button
          type="primary"
          disabled={!selectedIntentOptions?.length}
          onClick={() => onNextPressed(selectedIntentOptions)}
        >
          {$t('common.next')}
        </Button>
      </div>
    </Page>
  )
}

export default IntentPage
